import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { maximumPasswordLength, minimumPasswordLength } from "ui/Scripts/source/models/consts";
import { ApplicationPermissionType, ConfirmationDialogResult, EnumHelper, UnitPermissionApplicationPermissionTypes } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveAttachmentBase64 } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { ApplicationPermissionSave, IApplicationPermission } from "../../../models/common/applicationPermission";
import { ICodeTitle } from "../../../models/common/codeTitle";
import { ICompanyEdit } from "../../../models/company/companyEdit";
import { ICompanyEditItem } from "../../../models/company/companyEditItem";
import { ICompanyListItem } from "../../../models/company/companyListItem";
import { ICompanyGroupListItem } from "../../../models/companyGroup/companyGroupListItem";
import { IUserExternalSystem, UserSaveExternalSystem } from "../../../models/externalSystem/userExternalSystem";
import { IFileFilterListItem } from "../../../models/fileFilter/fileFilterListItem";
import { CompanySaveIntraModule, ICompanyIntraModule } from "../../../models/intraModule/companyIntraModule";
import { IMessageTypeListItem } from "../../../models/messageType/messageTypeListItem";
import * as companyService from "../../../models/services/companyService";
import { ISessionState } from "../../../models/session/sessionState";
import { IUnitModuleNode, UnitModuleNode } from "../../../models/unit/unitModuleNode";
import { UnitModuleNodeSave } from "../../../models/unit/unitModuleNodeSave";
import { IUnitModuleNodeUsage, UnitModuleNodeUsage } from "../../../models/unit/unitModuleNodeUsage";
import { CompanyUnitDialog } from "../Company/CompanyUnitDialog";
import { CompanyEditBasicInformation } from "./CompanyEditBasicInformation";
import { CompanyEditCompanyGroupList } from "./CompanyEditCompanyGroupList";
import { CompanyEditExternalSystems } from "./CompanyEditExternalSystems";
import { CompanyEditIntraModules } from "./CompanyEditIntraModules";
import { CompanyEditNewCompany } from "./CompanyEditNewCompany/CompanyEditNewCompany";
import { CompanyEditPartnerCompanyList } from "./CompanyEditPartnerCompanyList";
import { CompanyEditPermissions } from "./CompanyEditPermissions";

/* eslint-disable no-unused-vars */
export const enum CompanyEditViewMode {
    General = "1",
    NewCompany = "2",
    Permissions = "3",
    CompanyGroups = "4",
    Partners = "5",
    // Units = "6",
    ExternalSystems = "7",
    IntraModules = "8"
}
/* eslint-enable no-unused-vars */

//CompanyEdit
export interface ICompanyEditProp {
    sessionState: ISessionState;
    companyEdit?: ICompanyEdit;
    companyId: string;
    onDialog: boolean;
    viewMode: CompanyEditViewMode;
    onCompanyLoaded?: (company: ICompanyEditItem) => void;
    onRefreshSessionState: () => void;
    onAfterSave?: (closeDialog: boolean, styleChanged: boolean) => void;
}

export interface ICompanyEditState {
    isLoading: boolean;
    companyDataWasModified: boolean;
    company: ICompanyEditItem;
    logoFile: IFileAttachment;
    resetLogo: boolean;
    logoSquareFile: IFileAttachment;
    resetLogoSquare: boolean;
    defaultLogo: string;
    usedFileStorageQuota: number;
    customStyles: ICodeTitle[];
    //Company data
    id: string;
    rowId: string;
    name: string;
    postalAddress: string;
    postalCode: string;
    city: string;
    phone: string;
    email: string;
    internet: string;
    pageFooterText: string;
    businessIdentityCode: string;
    contactPerson: string;
    logo: string;
    logoSquare: string;
    styleClass: string;
    usedStoragePercent: number;
    eInvoiceAddress: string;
    eInvoiceOperator: string;
    minPasswordLength: number;
    //Admin Company data
    code: string;
    loginUrl: string;
    loginAllowMicrosoft: boolean;
    fileStorageQuota: string;
    customStyleClass: string;
    tiedostoturvaUseType: number;
    activeState: number;
    documentRootPath: string;
    bannerPath: string;
    documentPreviewLogType: number;
    //Units
    canEditWhistleBlowSettings: boolean;
    unitModuleNode: IUnitModuleNode;
    unitModuleNodes: IUnitModuleNode[];
    editUnitModuleNode: IUnitModuleNode;
    selectedUnitModuleNode: IUnitModuleNode;
    showUnitDialog: boolean;
    showUnitDialogForNew: boolean;
    saveUnitNodeIds: string[];
    removeUnitNodeIds: string[];
    unitDialogIsReadOnly: boolean;
    //Permissions
    applicationPermissions: IApplicationPermission[];
    //CompanyGroups
    companyGroupsEnabled: boolean;
    companyGroups: ICompanyGroupListItem[];
    //PartnerCompanies
    partnerCompaniesEnabled: boolean;
    partnerCompanies: ICompanyListItem[];
    allPartnersCompanyGroupId: string;
    //ExternalSystems
    externalSystems: IUserExternalSystem[];
    //IntraModules
    intraModules: ICompanyIntraModule[];
    //New Company
    fileFilters: IFileFilterListItem[];
    messageTypes: IMessageTypeListItem[];
    administratorCode: string;
    administratorFirstName: string;
    administratorLastName: string;
    administratorPassword: string;
    copyDocumentsFromCompanyCode: string;
    fileFilterIds: string[];
    messageTypeIds: string[];
}

export class CompanyEdit extends React.Component<ICompanyEditProp, ICompanyEditState> {
    private orgStateHash: string = "";

    getUsedStoragePercent = (usedFileStorageQuota: number, fileStorageQuota: number): number => {
        if (!usedFileStorageQuota || !fileStorageQuota) return 0;
        return usedFileStorageQuota > 0 ? Math.min(100, (usedFileStorageQuota / fileStorageQuota) * 100) : 0;
    };

    getStateFromCompanyEdit = (oldState: ICompanyEditState, companyEdit?: ICompanyEdit) => {
        const company = companyEdit.company;
        const selectedUnitId = this.state.selectedUnitModuleNode?.id;
        return {
            ...oldState,
            company,
            resetLogo: companyEdit.defaultLogoInUse,
            resetLogoSquare: !company.logoSquare,
            defaultLogo: companyEdit.defaultLogo,
            usedFileStorageQuota: companyEdit.usedFileStorageQuota,
            customStyles: companyEdit.customStyles,
            //Company data
            id: company.id,
            rowId: company.rowId,
            name: company.name,
            postalAddress: company.postalAddress,
            postalCode: company.postalCode,
            city: company.city,
            phone: company.phone,
            email: company.email,
            internet: company.internet,
            pageFooterText: company.pageFooterText,
            businessIdentityCode: company.businessIdentityCode,
            contactPerson: company.contactPerson,
            logo: company.logo,
            logoSquare: company.logoSquare
                ? company.logoSquare
                : (companyEdit.defaultLogoInUse
                    ? companyEdit.defaultLogo
                    : company.logo),
            styleClass: company.styleClass,
            usedStoragePercent: this.getUsedStoragePercent(companyEdit.usedFileStorageQuota, company.fileStorageQuota),
            eInvoiceAddress: company.eInvoiceAddress,
            eInvoiceOperator: company.eInvoiceOperator,
            minPasswordLength: company.minPasswordLength,
            //Admin Company data
            code: company.code,
            loginUrl: company.loginUrl,
            loginAllowMicrosoft: company.loginAllowMicrosoft,
            fileStorageQuota: company.fileStorageQuota ? company.fileStorageQuota.toString(10) : "0",
            customStyleClass: company.customStyleClass,
            tiedostoturvaUseType: company.tiedostoturvaUseType,
            activeState: company.activeState,
            documentRootPath: company.documentRootPath,
            bannerPath: company.bannerPath,
            documentPreviewLogType: company.documentPreviewLogType,
            //Units
            canEditWhistleBlowSettings: companyEdit.canEditWhistleBlowSettings,
            unitModuleNode: companyEdit.unitModuleNode,
            unitModuleNodes: companyEdit.unitModuleNode.getAsList([], 0),
            editUnitModuleNode: null,
            selectedUnitModuleNode: selectedUnitId ? companyEdit.unitModuleNode.getUnitModuleNodeById(selectedUnitId) : companyEdit.unitModuleNode,
            saveUnitNodeIds: [],
            removeUnitNodeIds: [],
            unitDialogIsReadOnly: false,
            //Permissions
            applicationPermissions: companyEdit.applicationPermissions.slice(0),
            //CompanyGroups
            companyGroupsEnabled: companyEdit.companyGroupsEnabled,
            companyGroups: companyEdit.companyGroups.slice(0),
            //PartnerCompanies
            partnerCompaniesEnabled: companyEdit.partnerCompaniesEnabled,
            partnerCompanies: companyEdit.partnerCompanies.slice(0),
            allPartnersCompanyGroupId: companyEdit.allPartnersCompanyGroupId,
            //ExternalSystems
            externalSystems: companyEdit.externalSystems.slice(0),
            //IntraModules
            intraModules: companyEdit.intraModules.slice(0),
            //New Company
            fileFilters: companyEdit.fileFilters.slice(0),
            messageTypes: companyEdit.messageTypes.slice(0),
            administratorCode: "",
            administratorFirstName: "",
            administratorLastName: "",
            administratorPassword: "",
            copyDocumentsFromCompanyCode: "",
            fileFilterIds: companyEdit.fileFilters.map(i => i.id),
            messageTypeIds: companyEdit.messageTypes.map(i => i.id),
        };
    };

    constructor(props: ICompanyEditProp) {
        super(props);
        this.state = {
            isLoading: false,
            companyDataWasModified: false,
            company: null,
            logoFile: null,
            resetLogo: false,
            logoSquareFile: null,
            resetLogoSquare: false,
            defaultLogo: "",
            usedFileStorageQuota: 0,
            customStyles: [],
            //Company data
            id: "",
            rowId: "",
            name: "",
            postalAddress: "",
            postalCode: "",
            city: "",
            phone: "",
            email: "",
            internet: "",
            pageFooterText: "",
            businessIdentityCode: "",
            contactPerson: "",
            logo: "",
            logoSquare: "",
            styleClass: "",
            usedStoragePercent: 0,
            eInvoiceAddress: "",
            eInvoiceOperator: "",
            minPasswordLength: minimumPasswordLength,
            //Admin Company data
            code: "",
            loginUrl: "",
            loginAllowMicrosoft: false,
            fileStorageQuota: "",
            customStyleClass: "",
            tiedostoturvaUseType: 0,
            activeState: 0,
            documentRootPath: "",
            bannerPath: "",
            documentPreviewLogType: 0,
            //Units
            canEditWhistleBlowSettings: false,
            unitModuleNode: null,
            unitModuleNodes: [],
            editUnitModuleNode: null,
            selectedUnitModuleNode: null,
            showUnitDialog: false,
            showUnitDialogForNew: false,
            saveUnitNodeIds: [],
            removeUnitNodeIds: [],
            unitDialogIsReadOnly: false,
            //Permissions
            applicationPermissions: [],
            //CompanyGroups
            companyGroupsEnabled: false,
            companyGroups: [],
            //PartnerCompanies
            partnerCompaniesEnabled: false,
            partnerCompanies: [],
            allPartnersCompanyGroupId: "",
            //ExternalSystems
            externalSystems: [],
            //IntraModules
            intraModules: [],
            //New Company
            fileFilters: [],
            messageTypes: [],
            administratorCode: "",
            administratorFirstName: "",
            administratorLastName: "",
            administratorPassword: "",
            copyDocumentsFromCompanyCode: "",
            fileFilterIds: [],
            messageTypeIds: []
        };
        if (!props.companyEdit) return;
        this.state = this.getStateFromCompanyEdit(this.state, props.companyEdit);
        this.getSaveDataFromState(props, this.state)
            .then(saveData => {
                this.orgStateHash = saveData.hash;
            });
    }

    loadCompany = async () => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => companyService.getCompanyEdit(props.companyId), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const state = this.getStateFromCompanyEdit(this.state, result);
        this.setState(state);
        const saveData = await this.getSaveDataFromState(props, state);
        this.orgStateHash = saveData.hash;
        if (props.onCompanyLoaded) {
            props.onCompanyLoaded(result.company);
        }
    };

    getCompany = (): ICompanyEditItem => {
        return this.state.company;
    };

    getCompanyGroupsEnabled = (): boolean => {
        return this.state.companyGroupsEnabled;
    };

    getPartnerCompaniesEnabled = (): boolean => {
        return this.state.partnerCompaniesEnabled;
    };

    getExternalSystemsExists = (): boolean => {
        return this.state.externalSystems.length > 0;
    };
    
    getExternalIntraSystemsExists = (): boolean => {
        return this.state.intraModules.length > 0;
    };
    
    getCompanyDataWasModified = (): boolean => {
        return this.state.companyDataWasModified;
    };

    cancel = async (closeDialog: boolean): Promise<ConfirmationDialogResult> => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb(closeDialog)) return null;
        }
        return cancelResult;
    };

    canNavigateOut = async (): Promise<boolean> => {
        const cancelResult = await this.cancel(true);
        if (cancelResult === ConfirmationDialogResult.Cancel) return Base.getPromiseResult(false);
        return Base.getPromiseResult(true);
    };

    componentDidMount(): void {
        const props = this.props;
        if (props.companyEdit) {
            if (props.onCompanyLoaded) {
                props.onCompanyLoaded(props.companyEdit.company);
            }
            return;
        }
        this.loadCompany();
    }

    async componentDidUpdate(prevProps: ICompanyEditProp, prevState: ICompanyEditState): Promise<void> {
        const props = this.props;
        const state = this.state;
        if (prevProps.companyId === props.companyId) return;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            if (!await this.saveToDb(false)) return;
        }
        this.loadCompany();
    }

    handleChange = (name: string, value: string) => {
        if (!name) return;
        if (name === "code") {
            this.setState({ code: value });
        } else if (name === "name") {
            this.setState({ name: value });
        } else if (name === "postalAddress") {
            this.setState({ postalAddress: value });
        } else if (name === "postalCode") {
            this.setState({ postalCode: value });
        } else if (name === "city") {
            this.setState({ city: value });
        } else if (name === "phone") {
            this.setState({ phone: value });
        } else if (name === "email") {
            this.setState({ email: value });
        } else if (name === "internet") {
            this.setState({ internet: value });
        } else if (name === "businessIdentityCode") {
            this.setState({ businessIdentityCode: value });
        } else if (name === "contactPerson") {
            this.setState({ contactPerson: value });
        } else if (name === "activeState") {
            this.setState({ activeState: value.toInteger() });
        } else if (name === "styleClass") {
            this.setState({ styleClass: value });
        } else if (name === "customStyleClass") {
            this.setState({ customStyleClass: value });
        } else if (name === "documentRootPath") {
            this.setState({ documentRootPath: value });
        } else if (name === "bannerPath") {
            this.setState({ bannerPath: value });
        } else if (name === "pageFooterText") {
            this.setState({ pageFooterText: value });
        } else if (name === "loginUrl") {
            this.setState({ loginUrl: value });
        } else if (name === "loginAllowMicrosoft") {
            this.setState({ loginAllowMicrosoft: value.toInteger() > 0 });
        } else if (name === "eInvoiceAddress") {
            this.setState({ eInvoiceAddress: value });
        } else if (name === "eInvoiceOperator") {
            this.setState({ eInvoiceOperator: value });
        } else if (name === "documentPreviewLogType") {
            this.setState({ documentPreviewLogType: value.toInteger() });
        } else if (name === "tiedostoturvaUseType") {
            this.setState({ tiedostoturvaUseType: value.toInteger() });
        } else if (name === "fileStorageQuota") {
            this.setState({
                fileStorageQuota: value,
                usedStoragePercent: this.getUsedStoragePercent(this.state.usedFileStorageQuota, value.toInteger())
            });
        } else if (name === "administratorCode") {
            this.setState({ administratorCode: value });
        } else if (name === "administratorPassword") {
            this.setState({ administratorPassword: value });
        } else if (name === "administratorFirstName") {
            this.setState({ administratorFirstName: value });
        } else if (name === "administratorLastName") {
            this.setState({ administratorLastName: value });
        } else if (name === "copyDocumentsFromCompanyCode") {
            this.setState({ copyDocumentsFromCompanyCode: value });
        } else if (name === "minPasswordLength") {
            this.setState({ minPasswordLength: value.toInteger() });
        }
    };

    handleChangeLogo = async (main: boolean, file: File) => {
        if (!file) return;
        const state = this.state;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!AppUtils.validateFileAttachment(attachment)) return;
        if (main) {
            this.setState({
                logoFile: attachment,
                resetLogo: false,
                logo: attachment.thumbnail,
                logoSquare: !state.resetLogoSquare
                    ? state.logoSquare
                    : attachment.thumbnail
            });
        } else {
            this.setState({
                logoSquareFile: attachment,
                resetLogoSquare: false,
                logoSquare: attachment.thumbnail
            });
        }
    };

    handleRemoveLogo = (main: boolean) => {
        const state = this.state;
        if (main) {
            this.setState({
                logoFile: null,
                resetLogo: true,
                logo: state.defaultLogo,
                logoSquare: !state.resetLogoSquare
                    ? state.logoSquare
                    : state.defaultLogo
            });
        } else {
            this.setState({
                logoSquareFile: null,
                resetLogoSquare: true,
                logoSquare: !state.resetLogo
                    ? state.logo
                    : state.defaultLogo
            });
        }
    };

    // #region New company
    handleSetMessageTypeIds = (ids: string[]) => {
        this.setState({
            messageTypeIds: ids
        });
    };

    handleSetFileFilterIds = (ids: string[]) => {
        this.setState({
            fileFilterIds: ids
        });
    };
    // #endregion New company

    // #region Units
    handleUnitNodeClick = (id: string) => {
        if (!id) return;
        const selectedUnitModuleNode = this.state.unitModuleNode.getUnitModuleNodeById(id);
        if (!selectedUnitModuleNode) return;
        this.setState({
            selectedUnitModuleNode
        });
    };

    handleEditUnitNode = () => {
        const state = this.state;
        if (!state.selectedUnitModuleNode) return;
        const whistleBlowUsage = state.selectedUnitModuleNode.usages.find(i => EnumHelper.isEqual(i.applicationPermissionType, ApplicationPermissionType.WhistleBlow));
        this.setState({
            showUnitDialog: true,
            showUnitDialogForNew: false,
            editUnitModuleNode: state.selectedUnitModuleNode,
            unitDialogIsReadOnly: whistleBlowUsage && whistleBlowUsage.inUse && !state.canEditWhistleBlowSettings
        });
    };

    handleAddUnitNode = () => {
        const state = this.state;
        if (!state.selectedUnitModuleNode) return;
        if (!state.selectedUnitModuleNode.getIsRoot()) {
            const whistleBlowUsage = state.selectedUnitModuleNode.usages.find(i => EnumHelper.isEqual(i.applicationPermissionType, ApplicationPermissionType.WhistleBlow));
            if (whistleBlowUsage && whistleBlowUsage.inUse && !state.canEditWhistleBlowSettings) {
                AppUtils.showErrorMessage(Translations.PermissionDeniedForOperation);
                return;
            }
        }
        const editUnitModuleNode = new UnitModuleNode();
        editUnitModuleNode.id = Base.getGuid();
        editUnitModuleNode.parent = state.selectedUnitModuleNode;
        editUnitModuleNode.usages = state.selectedUnitModuleNode.usages.map(i => new UnitModuleNodeUsage(i));
        for (const usage of editUnitModuleNode.usages) {
            if (EnumHelper.isEqual(usage.applicationPermissionType, ApplicationPermissionType.WhistleBlow) && !state.canEditWhistleBlowSettings) {
                usage.inUse = false;
            }
            if (UnitPermissionApplicationPermissionTypes.indexOf(usage.applicationPermissionType) > -1) continue;
            usage.enabled = false;
        }
        this.setState({
            showUnitDialog: true,
            showUnitDialogForNew: true,
            unitDialogIsReadOnly: false,
            editUnitModuleNode,
        });
    };

    handleSetUsage = (id: string, applicationPermissionType: ApplicationPermissionType, inUse: boolean) => {
        const state = this.state;
        if (!id) return;
        const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
        const selectedUnitModuleNode = unitModuleNode.getUnitModuleNodeById(id);
        if (!selectedUnitModuleNode) return;
        const usage = new UnitModuleNodeUsage();
        usage.applicationPermissionType = applicationPermissionType;
        usage.inUse = inUse;
        const modifiedUnitIds = selectedUnitModuleNode.setUsages([usage]);
        this.setState({
            selectedUnitModuleNode,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([]),
            saveUnitNodeIds: state.saveUnitNodeIds.filter(i => modifiedUnitIds.indexOf(i) < 0).concat(modifiedUnitIds)
        });
    };

    handleSetCollapsed = (id: string, value: boolean) => {
        const state = this.state;
        if (!id) return;
        const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
        const selectedUnitModuleNode = unitModuleNode.getUnitModuleNodeById(id);
        if (!selectedUnitModuleNode) return;
        selectedUnitModuleNode.collapsed = value;
        this.setState({
            selectedUnitModuleNode,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([])
        });
    };

    handleCompanyUnitDialogOk = (name: string, comment: string, usages: IUnitModuleNodeUsage[]) => {
        const state = this.state;
        if (!state.editUnitModuleNode) return;
        const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
        if (state.showUnitDialogForNew) {
            const selectedUnitModuleNode = unitModuleNode.getUnitModuleNodeById(state.selectedUnitModuleNode.id);
            if (!selectedUnitModuleNode) return;
            state.editUnitModuleNode.name = name;
            selectedUnitModuleNode.addUnitModuleNode(state.editUnitModuleNode);
        }
        const editUnitNode = unitModuleNode.getUnitModuleNodeById(state.editUnitModuleNode.id);
        if (!editUnitNode) return;
        editUnitNode.name = name;
        editUnitNode.comment = comment;
        const modifiedUnitIds = editUnitNode.setUsages(usages);
        this.setState({
            showUnitDialog: false,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([]),
            selectedUnitModuleNode: editUnitNode,
            saveUnitNodeIds: state.saveUnitNodeIds.filter(i => modifiedUnitIds.indexOf(i) < 0).concat(modifiedUnitIds)
        });
    };

    canRemoveUnitNode = (unitModuleNode: IUnitModuleNode): boolean => {
        if (!unitModuleNode) return false;
        const whistleBlowUsage = unitModuleNode.usages.find(i => EnumHelper.isEqual(i.applicationPermissionType, ApplicationPermissionType.WhistleBlow));
        return !unitModuleNode.getIsRoot() && !unitModuleNode.getHasChildren() && !(whistleBlowUsage && whistleBlowUsage.inUse && !this.state.canEditWhistleBlowSettings);
    };

    handleRemoveUnitNode = async () => {
        const state = this.state;
        if (!this.canRemoveUnitNode(state.selectedUnitModuleNode)) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveUnitParameter, state.selectedUnitModuleNode.name)) === ConfirmationDialogResult.Yes) {
            let removeUnitNodeIds = state.removeUnitNodeIds.slice(0);
            const unitModuleNode = new UnitModuleNode(state.unitModuleNode);
            const newRemoveUnitNodeIds = unitModuleNode.removeUnitModuleNodeById(state.selectedUnitModuleNode.id);
            if (!state.selectedUnitModuleNode.isNew()) {
                removeUnitNodeIds = removeUnitNodeIds.concat(newRemoveUnitNodeIds);
            }
            const selectedUnitModuleNode = state.selectedUnitModuleNode ? unitModuleNode.getUnitModuleNodeById(state.selectedUnitModuleNode.parent.id) : null;
            this.setState({
                unitModuleNode,
                unitModuleNodes: unitModuleNode.getAsList([]),
                removeUnitNodeIds,
                saveUnitNodeIds: state.saveUnitNodeIds.filter(i => newRemoveUnitNodeIds.indexOf(i) < 0),
                selectedUnitModuleNode: selectedUnitModuleNode ?? unitModuleNode
            });
        }
    };

    handleCompanyUnitDialogCancel = () => {
        this.setState({
            showUnitDialog: false
        });
    };

    setUnitApplicationPermissionTypeVisible = (applicationPermissionType: number, value: boolean) => {
        const state = this.state;
        const unitModuleNode = new UnitModuleNode(this.state.unitModuleNode);
        unitModuleNode.setApplicationPermissionTypeVisible(applicationPermissionType, value);
        const selectedUnitModuleNode = state.selectedUnitModuleNode ? unitModuleNode.getUnitModuleNodeById(state.selectedUnitModuleNode.id) : null;
        this.setState({
            selectedUnitModuleNode,
            unitModuleNode,
            unitModuleNodes: unitModuleNode.getAsList([])
        });
    };
    // #endregion Units

    // #region ApplicationPermissions
    handleSetApplicationPermission = (applicationPermissionType: ApplicationPermissionType, value: boolean) => {
        const applicationPermissions = this.state.applicationPermissions.slice(0);
        const applicationPermission = applicationPermissions.find(i => EnumHelper.isEqual(i.type, applicationPermissionType));
        if (!applicationPermission) return;
        applicationPermission.level = value ? 2 : 0;
        this.setState({
            applicationPermissions
        });
        this.setUnitApplicationPermissionTypeVisible(applicationPermissionType, value);
    };
    // #endregion ApplicationPermissions

    // #region Company Groups
    handleCompanyGroupsRemoved = (ids: string[]) => {
        if (!ids || ids.length < 1) return;
        this.setState({
            companyGroups: this.state.companyGroups.filter(i => ids.indexOf(i.id) < 0),
            companyDataWasModified: true
        });
    };
    
    handleCompanyGroupsAdded = (newCompanyGroups: ICompanyGroupListItem[]) => {
        if (!newCompanyGroups || newCompanyGroups.length < 1) return;
        this.setState({
            companyGroups: this.state.companyGroups.concat(newCompanyGroups),
            companyDataWasModified: true
        });
    };
    // #endregion Company Groups

    // #region Partner Companies
    handlePartnerCompaniesRemoved = (ids: string[]) => {
        if (!ids || ids.length < 1) return;
        this.setState({
            partnerCompanies: this.state.partnerCompanies.filter(i => ids.indexOf(i.id) < 0),
            companyDataWasModified: true
        });
    };
    
    handlePartnerCompaniesAdded = (newPartnerCompanies: ICompanyListItem[]) => {
        if (!newPartnerCompanies || newPartnerCompanies.length < 1) return;
        this.setState({
            partnerCompanies: this.state.partnerCompanies.concat(newPartnerCompanies),
            companyDataWasModified: true
        });
    };
    // #endregion Partner Companies

    // #region ExternalSystems
    handleToggeExternalSystemInUse = (code: string, value: boolean) => {
        if (!code) return;
        const externalSystems = this.state.externalSystems.slice(0);
        const externalSystem = externalSystems.find(i => i.code === code);
        if (!externalSystem) return;
        externalSystem.inUse = value;
        this.setState({ externalSystems });
    };

    handleChangeExternalSystemExternalCode = (code: string, value: string) => {
        if (!code) return;
        const externalSystems = this.state.externalSystems.slice(0);
        const externalSystem = externalSystems.find(i => i.code === code);
        if (!externalSystem) return;
        externalSystem.externalCode = value;
        this.setState({ externalSystems });
    };
    // #endregion ExternalSystems

    // #region IntraModules
    handleToggeIntraModuleInUse = (code: string, value: boolean) => {
        if (!code) return;
        const intraModules = this.state.intraModules.slice(0);
        const intraModule = intraModules.find(i => i.code === code);
        if (!intraModule) return;
        intraModule.inUse = value;
        this.setState({ intraModules });
    };

    handleChangeIntraModuleExternalCode = (code: string, value: string) => {
        if (!code) return;
        const intraModules = this.state.intraModules.slice(0);
        const intraModule = intraModules.find(i => i.code === code);
        if (!intraModule) return;
        intraModule.externalCode = value;
        this.setState({ intraModules });
    };
    // #endregion IntraModules

    getSaveDataFromState = async (props: ICompanyEditProp, state: ICompanyEditState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", state.id);
        result.append("rowId", state.rowId);
        result.append("code", state.code);
        result.append("name", state.name);
        result.append("postalAddress", state.postalAddress);
        result.append("postalCode", state.postalCode);
        result.append("city", state.city);
        result.append("phone", state.phone);
        result.append("email", state.email);
        result.append("internet", state.internet);
        result.append("businessIdentityCode", state.businessIdentityCode);
        result.append("contactPerson", state.contactPerson);
        result.append("pageFooterText", state.pageFooterText);
        result.append("styleClass", state.styleClass);
        result.append("customStyleClass", state.customStyleClass);
        result.append("documentRootPath", state.documentRootPath);
        result.append("bannerPath", state.bannerPath);
        result.append("loginUrl", state.loginUrl);
        result.append("loginAllowMicrosoft", state.loginAllowMicrosoft);
        result.append("tiedostoturvaUseType", state.tiedostoturvaUseType);
        result.append("activeState", state.activeState);
        result.append("documentPreviewLogType", state.documentPreviewLogType);
        result.append("fileStorageQuota", state.fileStorageQuota);
        result.append("eInvoiceAddress", state.eInvoiceAddress);
        result.append("eInvoiceOperator", state.eInvoiceOperator);
        result.append("resetLogo", state.resetLogo);
        result.append("resetLogoSquare", state.resetLogoSquare);
        if (state.logoFile) {
            result.append("logoAttachments", await SaveAttachmentBase64.fromFileAttachments([state.logoFile]));
        }
        if (state.logoSquareFile) {
            result.append("logoSquareAttachments", await SaveAttachmentBase64.fromFileAttachments([state.logoSquareFile]));
        }
        result.append("minPasswordLength", state.minPasswordLength);
        // units
        const saveUnitNodeIds = state.saveUnitNodeIds.slice(0);
        for (const unitModuleNode of state.unitModuleNodes) {
            if (unitModuleNode.getIsRoot()) continue;
            let modified = false;
            for (const usage of unitModuleNode.usages) {
                if (usage.visible) continue;
                if (!usage.inUse) continue;
                usage.inUse = false;
                modified = true;
            }
            if (modified && saveUnitNodeIds.indexOf(unitModuleNode.id) > -1) {
                saveUnitNodeIds.push(unitModuleNode.id);
            }
        }
        result.append("units", state.unitModuleNodes.reduce((result, unitModuleNode) => {
            if (saveUnitNodeIds.indexOf(unitModuleNode.id) > -1) {
                result.push(new UnitModuleNodeSave(unitModuleNode));
            }
            return result;
        }, []));
        result.append("removeUnitIds", state.removeUnitNodeIds);
        // application permissions
        result.append("applicationPermissions", state.applicationPermissions.map(i => new ApplicationPermissionSave(i)));
        // externalSystems
        result.append("externalSystems", state.externalSystems.reduce((result, externalSystem) => {
            if (externalSystem.inUse) {
                result.push(new UserSaveExternalSystem(externalSystem));
            }
            return result;
        }, []));
        // externalSystems
        result.append("intraModules", state.intraModules.reduce((result, intraModule) => {
            if (intraModule.inUse) {
                result.push(new CompanySaveIntraModule(intraModule));
            }
            return result;
        }, []));
        // new company
        result.append("administratorCode", state.administratorCode);
        result.append("administratorPassword", state.administratorPassword);
        result.append("administratorFirstName", state.administratorFirstName);
        result.append("administratorLastName", state.administratorLastName);
        result.append("copyDocumentsFromCompanyCode", state.copyDocumentsFromCompanyCode);
        result.append("fileFilterIds", state.fileFilterIds);
        result.append("messageTypeIds", state.messageTypeIds);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.code) {
                AppUtils.showErrorMessage(Translations.CompanyCodeMustBeDefined);
                return resolve(false);
            }
            if (!state.name) {
                AppUtils.showErrorMessage(Translations.CompanyNameMustBeDefined);
                return resolve(false);
            }
            if (!Base.isValidEmailAddress(state.email)) {
                AppUtils.showErrorMessage(Translations.EmailAddressMustBeValid);
                return resolve(false);
            }
            if (state.minPasswordLength < minimumPasswordLength) {
                AppUtils.showErrorMessage(String.format(Translations.MinimumPasswordLengthMustBeAtLeastParameterCharacters, minimumPasswordLength.toString(10)));
                return resolve(false);
            }
            if (state.minPasswordLength > maximumPasswordLength) {
                AppUtils.showErrorMessage(String.format(Translations.MaximumPasswordLengthCanParameterCharacters, maximumPasswordLength.toString(10)));
                return resolve(false);
            }
            if (state.company.isNew()) {
                if (!state.administratorCode) {
                    AppUtils.showErrorMessage(Translations.AdministratorUserCodeMustBeDefined);
                    return resolve(false);
                }
                if (!state.administratorPassword) {
                    AppUtils.showErrorMessage(Translations.AdministratorPasswordMustBeDefined);
                    return resolve(false);
                }
                if (!Base.isValidUserCode(state.administratorCode)) {
                    AppUtils.showErrorMessage(Translations.UserCodeMustBeValid);
                    return resolve(false);
                }
                const errorMessage = Base.isValidPassword(state.administratorPassword, state.minPasswordLength);
                if (errorMessage) {
                    AppUtils.showErrorMessage(errorMessage);
                    return resolve(false);
                }
            }
            if (props.onDialog && props.sessionState.isSuperAdmin && !state.documentRootPath) {
                AppUtils.showErrorMessage(Translations.DocumentRootPathMustBeDefined);
                return resolve(false);
            }
            for (const externalSystem of state.externalSystems) {
                if (!externalSystem.inUse) continue;
                if (!externalSystem.externalCode) {
                    AppUtils.showErrorMessage(Translations.ExternalSystemCompanyCodeMustBeDefined);
                    return resolve(false);
                }
            }
            for (const intraModule of state.intraModules) {
                if (!intraModule.inUse) continue;
                if (intraModule.usesExternalCode && !intraModule.externalCode) {
                    AppUtils.showErrorMessage(Translations.ExternalIntraSystemCompanyTokenMustBeDefined);
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    saveToDbUser = async (closeDialog: boolean): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => companyService.saveCompanyEdit(saveData.obj));
        if (!result) return null;
        props.onRefreshSessionState();
        this.orgStateHash = saveData.hash;
        this.setState({
            rowId: result.rowId
        });
        await this.loadCompany();
        if (!props.onAfterSave) {
            if (result.styleChanged) {
                window.location.reload();
            }
            return !!result;
        }
        props.onAfterSave(closeDialog, result.styleChanged);
        return !!result;
    };

    saveToDbAdmin = async (closeDialog: boolean): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => companyService.saveCompanyEditAdmin(saveData.obj));
        if (!result) return null;
        if (result.refreshSessionState) {
            props.onRefreshSessionState();
        }
        const newSaveData = await this.getSaveDataFromState(props, { ...state, rowId: result.rowId });
        this.orgStateHash = newSaveData.hash;
        this.setState({
            rowId: result.rowId
        });
        props.onAfterSave(closeDialog, result.styleChanged);
        return !!result;
    };

    saveToDb = async (closeDialog: boolean): Promise<boolean> => {
        return !this.props.onDialog
            ? await this.saveToDbUser(closeDialog)
            : await this.saveToDbAdmin(closeDialog);
    };

    save = async () => {
        await this.saveToDb(true);
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.company) return null;
        return (
            <>
                {props.viewMode === CompanyEditViewMode.General &&
                    <CompanyEditBasicInformation
                        sessionState={props.sessionState}
                        company={state.company}
                        customStyles={state.customStyles}
                        adminMode={props.onDialog && props.sessionState.isSuperAdmin}
                        usedFileStorageQuota={state.usedFileStorageQuota}
                        //Company data
                        name={state.name}
                        postalAddress={state.postalAddress}
                        postalCode={state.postalCode}
                        city={state.city}
                        phone={state.phone}
                        email={state.email}
                        internet={state.internet}
                        pageFooterText={state.pageFooterText}
                        businessIdentityCode={state.businessIdentityCode}
                        contactPerson={state.contactPerson}
                        logo={state.logo}
                        resetLogo={state.resetLogo}
                        logoSquare={state.logoSquare}
                        resetLogoSquare={state.resetLogoSquare}
                        styleClass={state.styleClass}
                        usedStoragePercent={state.usedStoragePercent}
                        eInvoiceAddress={state.eInvoiceAddress}
                        eInvoiceOperator={state.eInvoiceOperator}
                        minPasswordLength={state.minPasswordLength}
                        //Admin Company data
                        code={state.code}
                        loginUrl={state.loginUrl}
                        loginAllowMicrosoft={state.loginAllowMicrosoft}
                        fileStorageQuota={state.fileStorageQuota}
                        customStyleClass={state.customStyleClass}
                        tiedostoturvaUseType={state.tiedostoturvaUseType}
                        activeState={state.activeState}
                        documentRootPath={state.documentRootPath}
                        bannerPath={state.bannerPath}
                        documentPreviewLogType={state.documentPreviewLogType}
                        onChange={this.handleChange}
                        onChangeLogo={this.handleChangeLogo}
                        onRemoveLogo={this.handleRemoveLogo}
                    />
                }
                {/* {props.viewMode === CompanyEditViewMode.Units &&
                    <CompanyEditUnits
                        sessionState={props.sessionState}
                        unitModuleNodes={state.unitModuleNodes}
                        selectedUnitId={state.selectedUnitModuleNode?.id}
                        removeDisabled={!this.canRemoveUnitNode(state.selectedUnitModuleNode)}
                        canEditWhistleBlowSettings={state.canEditWhistleBlowSettings}
                        onUnitClick={this.handleUnitNodeClick}
                        onUnitEdit={this.handleEditUnitNode}
                        onUnitAdd={this.handleAddUnitNode}
                        onUnitRemove={this.handleRemoveUnitNode}
                        onSetUsage={this.handleSetUsage}
                        onSetCollapsed={this.handleSetCollapsed}
                    />
                } */}
                {props.viewMode === CompanyEditViewMode.NewCompany &&
                    <CompanyEditNewCompany
                        fileFilters={state.fileFilters}
                        messageTypes={state.messageTypes}
                        administratorCode={state.administratorCode}
                        administratorFirstName={state.administratorFirstName}
                        administratorLastName={state.administratorLastName}
                        administratorPassword={state.administratorPassword}
                        copyDocumentsFromCompanyCode={state.copyDocumentsFromCompanyCode}
                        fileFilterIds={state.fileFilterIds}
                        messageTypeIds={state.messageTypeIds}
                        onChange={this.handleChange}
                        onSetMessageTypeIds={this.handleSetMessageTypeIds}
                        onSetFileFilterIds={this.handleSetFileFilterIds}
                    />
                }
                {props.viewMode === CompanyEditViewMode.Permissions &&
                    <CompanyEditPermissions
                        applicationPermissions={state.applicationPermissions}
                        onSetValue={this.handleSetApplicationPermission}
                    />
                }
                {props.viewMode === CompanyEditViewMode.CompanyGroups &&
                    <CompanyEditCompanyGroupList
                        isReadOnly={state.company.isNew()}
                        companyId={state.id}
                        companyGroups={state.companyGroups}
                        onRemoved={this.handleCompanyGroupsRemoved}
                        onAdded={this.handleCompanyGroupsAdded}
                    />
                }
                {props.viewMode === CompanyEditViewMode.Partners &&
                    <CompanyEditPartnerCompanyList
                        isReadOnly={state.company.isNew()}
                        companyId={state.id}
                        companies={state.partnerCompanies}
                        allPartnersCompanyGroupId={state.allPartnersCompanyGroupId}
                        onRemoved={this.handlePartnerCompaniesRemoved}
                        onAdded={this.handlePartnerCompaniesAdded}
                    />
                }
                {props.viewMode === CompanyEditViewMode.ExternalSystems &&
                    <CompanyEditExternalSystems
                        externalSystems={state.externalSystems}
                        onToggeExternalSystemInUse={this.handleToggeExternalSystemInUse}
                        onChangeExternalSystemExternalCode={this.handleChangeExternalSystemExternalCode}
                    />
                }
                {props.viewMode === CompanyEditViewMode.IntraModules &&
                    <CompanyEditIntraModules
                        intraModules={state.intraModules}
                        onToggeIntraModuleInUse={this.handleToggeIntraModuleInUse}
                        onChangeIntraModuleExternalCode={this.handleChangeIntraModuleExternalCode}
                    />
                }
                {state.showUnitDialog &&
                    <CompanyUnitDialog
                        sessionState={props.sessionState}
                        unitModuleNode={state.editUnitModuleNode}
                        canEditWhistleBlowSettings={state.canEditWhistleBlowSettings}
                        isNew={state.showUnitDialogForNew}
                        isReadOnly={state.unitDialogIsReadOnly}
                        onOk={this.handleCompanyUnitDialogOk}
                        onCancel={this.handleCompanyUnitDialogCancel}
                    />
                }
            </>
        );
    }
}